import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';

import type { GapDraftView } from '@momentum/types';

import type { GapLanguage } from '@/features/deal-intelligence/components/DealIntelligencePage';
import { GapDetail } from '@/features/deal-intelligence/components/DealIntelligencePage';
import { useGapReviewData } from '../gap-review-context';
import { draftToPreview } from '../lib/draft-to-gap';
import { DEFAULT_QUEUE_SEARCH } from '../lib/queue';
import { DEFAULT_READER_VIEW } from '../lib/reader-view';
import { PreviewFallbackBanner } from './PreviewFallbackBanner';

/** Where the preview was opened from, and therefore where Back returns to. */
export type PreviewOrigin = 'reader' | 'final';

export function isPreviewOrigin(value: unknown): value is PreviewOrigin {
  return value === 'reader' || value === 'final';
}

/** The reader, with this draft selected — the queue's own link shape. */
function readerSearchFor(draft: GapDraftView) {
  return {
    execution: draft.executionId,
    filter: 'all' as const,
    draft: draft._id,
    view: DEFAULT_READER_VIEW,
  };
}

/**
 * One draft as the customer-facing deal-intelligence page would render it.
 *
 * The real `GapDetail` is rendered, not a copy of it: a preview that drifts from
 * the app is worse than no preview, and the reason this view exists is to answer
 * "what would the customer actually see" — a question only the app's own
 * component can answer.
 *
 * Two things make that safe. The draft is adapted, not published: `draftToPreview`
 * builds the shape the component reads without touching the `gaps` collection.
 * And `interactive={false}` means the feedback row is not rendered at all, so
 * neither of its mutations mounts and no vote or comment can be written against
 * an id that is not a published gap.
 */
export function AppPreviewView({
  draftId,
  from,
}: {
  draftId: string;
  from: PreviewOrigin;
}) {
  const { drafts, executionId } = useGapReviewData();
  const navigate = useNavigate();
  // The app's own toggle, kept local: the preview is a look at one draft, and
  // the choice does not deserve to outlive it.
  const [language, setLanguage] = useState<GapLanguage>('en');

  const draft = drafts.find((candidate) => candidate._id === draftId);

  const goBack = useCallback(() => {
    if (from === 'final') {
      void navigate({
        to: '/gap-review/final',
        search: { execution: executionId },
      });
      return;
    }
    void navigate({
      to: '/gap-review',
      search:
        draft === undefined
          ? { ...DEFAULT_QUEUE_SEARCH, execution: executionId }
          : readerSearchFor(draft),
    });
  }, [draft, executionId, from, navigate]);

  // Esc leaves, matching the mockup's own preview chrome. Nothing else is bound:
  // the queue's shortcuts act on a selection this view does not have.
  useEffect(() => {
    function handleKeyDown(keyboardEvent: KeyboardEvent) {
      if (keyboardEvent.key === 'Escape') goBack();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [goBack]);

  if (draft === undefined) {
    return (
      <div className="gr-view gr-page">
        <div className="gr-page-in">
          <div className="gr-empty">
            <b>No such draft</b>
            {draftId} is not among the gaps of run {executionId}.
          </div>
          <Link
            className="gr-back"
            to="/gap-review"
            search={{ ...DEFAULT_QUEUE_SEARCH, execution: executionId }}
          >
            ← Back to the review queue
          </Link>
        </div>
      </div>
    );
  }

  const { flatGap, fromRun, missing } = draftToPreview(draft);

  return (
    <div className="gr-view gr-page">
      <div className="gr-appbar">
        <b>App preview</b>
        <span className="gr-appbar-note">
          The customer-facing page, read-only —{' '}
          <code>
            {draft.gap.externalRef?.externalId ?? draft.source.metadata.id}
          </code>{' '}
          as it would render in deal intelligence. The app&apos;s left rail is
          left out; everything inside the column is the real component.
        </span>
        <button className="gr-btn-ghost" type="button" onClick={goBack}>
          Back to {from === 'final' ? 'final review' : 'the reader'}{' '}
          <span className="gr-kbd">Esc</span>
        </button>
      </div>

      <PreviewFallbackBanner fromRun={fromRun} missing={missing} />

      {/*
        From here down the markup belongs to the app, so it is styled the app's
        way — Tailwind, and the deal-intelligence page's own gradient — rather
        than with the review stylesheet. `color-scheme: light` because the review
        chrome may be in dark mode and the component names light colours outright.
      */}
      <div className="[color-scheme:light] bg-[linear-gradient(180deg,#ffffff_0%,#f8fafc_60%,#f3f6fb_100%)]">
        <div className="relative isolate px-2.5 py-10 md:px-5 md:py-14">
          <GapDetail
            flatGap={flatGap}
            onBack={goBack}
            // The project route is a customer route on a published project; a
            // draft's scope may have no page there at all.
            showProjectLink={false}
            language={language}
            onLanguageChange={setLanguage}
            interactive={false}
          />
        </div>
      </div>
    </div>
  );
}
